import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useEmergencyCloseGateway } from 'src/features/monitoring/api/useEmergencyCloseGateway';
import { useEmergencyOpenGateway } from 'src/features/monitoring/api/useEmergencyOpenGateway';
import { useLockGateway } from 'src/features/monitoring/api/useLockGateway';
import { useOpenGateway } from 'src/features/monitoring/api/useOpenGateway';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { GatewayAction, GatewayActionState } from 'src/features/visitor/types';
import { toast } from 'src/hooks/UseToast';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    gatewayAction: GatewayActionState;
    setGatewayAction: (v: GatewayActionState) => void;
};

export default function GatewayActionModal({ gatewayAction, setGatewayAction }: Props) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();

    // A barrier action answers with whether the command actually reached the gateway. A core that
    // predates the fix for this reports a publish it could not make as a plain false on a 200
    // instead of an error, so the reply is checked before anything is announced as done. Strictly
    // false, never merely falsy - a body-less 200 is still a success, and treating it as a failure
    // would raise a false alarm on every action.
    const onCommandSent = (announce: () => void) => (isSent: boolean | undefined) => {
        if (isSent === false) {
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(parkingStatusMessages.gatewayCommandNotSent),
                variant: 'destructive',
            });
            return;
        }

        announce();
    };

    const { mutate: openGateway } = useOpenGateway({
        onSuccess: onCommandSent(() => {
            toast({
                title: formatMessage(parkingStatusMessages.gatewayBarrierRaised),
                description: formatMessage(parkingStatusMessages.gatewayBarrierRaisedDescription, {
                    entry: gatewayAction?.gateway.name,
                }),
            });
            setGatewayAction(null);
        }),
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { mutate: emergencyOpenGateway } = useEmergencyOpenGateway({
        onSuccess: onCommandSent(() => {
            toast({
                title: formatMessage(parkingStatusMessages.gatewayBarrierRaised),
                description: formatMessage(parkingStatusMessages.gatewayBarrierRaisedDescription, {
                    entry: gatewayAction?.gateway.name,
                }),
            });
            setGatewayAction(null);
        }),
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { mutate: emergencyCloseGateway } = useEmergencyCloseGateway({
        onSuccess: onCommandSent(() => {
            toast({
                title: formatMessage(parkingStatusMessages.gatewayBarrierClosed),
                description: formatMessage(parkingStatusMessages.gatewayBarrierClosedDescription, {
                    entry: gatewayAction?.gateway.name,
                }),
            });
            setGatewayAction(null);
        }),
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { mutate: lockGateway } = useLockGateway({
        onSuccess: () => {
            toast({
                title: formatMessage(parkingStatusMessages.gatewayBarrierLocked),
                description: formatMessage(parkingStatusMessages.gatewayBarrierLockedDescription, {
                    entry: gatewayAction?.gateway.name,
                }),
            });
            setGatewayAction(null);
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    React.useEffect(() => {
        setIsModalOpen(!!gatewayAction);
    }, [gatewayAction]);

    const { text, button, description, title, onSubmit, icon } = React.useMemo(() => {
        if (!gatewayAction) {
            return {
                icon: null,
                title: null,
                description: null,
                text: null,
                button: null,
                onSubmit: () => {},
            };
        }

        if (gatewayAction?.action === GatewayAction.LOCK) {
            return {
                icon: ICON_MAP.PASSWORD,
                title: formatMessage(parkingStatusMessages.lockGatewayBarrier, { entry: gatewayAction?.gateway.name }),
                description: formatMessage(parkingStatusMessages.lockGatewayBarrierDescription),
                text: formatMessage(parkingStatusMessages.lockGatewayBarrierText, {
                    entry: gatewayAction?.gateway.name,
                }),
                button: formatMessage(parkingStatusMessages.lock),
                onSubmit: () => lockGateway({ parkingId, gatewayId: gatewayAction.gateway.id }),
            };
        }

        if (gatewayAction?.action === GatewayAction.EMERGENCY) {
            return {
                icon: ICON_MAP.DANGER,
                title: formatMessage(parkingStatusMessages.emergencyRaiseGatewayBarrier, {
                    entry: gatewayAction?.gateway.name,
                }),
                description: formatMessage(parkingStatusMessages.emergencyRaiseGatewayBarrierDescription),
                text: formatMessage(parkingStatusMessages.emergencyRaiseGatewayBarrierText, {
                    entry: gatewayAction?.gateway.name,
                }),
                button: formatMessage(parkingStatusMessages.emergencyRaise),
                onSubmit: () => emergencyOpenGateway({ parkingId, gatewayId: gatewayAction.gateway.id }),
            };
        }

        if (gatewayAction?.action === GatewayAction.EMERGENCY_CLOSE) {
            return {
                icon: ICON_MAP.DANGER,
                title: formatMessage(parkingStatusMessages.emergencyCloseGatewayBarrier, {
                    entry: gatewayAction?.gateway.name,
                }),
                description: formatMessage(parkingStatusMessages.emergencyCloseGatewayBarrierDescription),
                text: formatMessage(parkingStatusMessages.emergencyCloseGatewayBarrierText, {
                    entry: gatewayAction?.gateway.name,
                }),
                button: formatMessage(parkingStatusMessages.emergencyClose),
                onSubmit: () => emergencyCloseGateway({ parkingId, gatewayId: gatewayAction.gateway.id }),
            };
        }

        return {
            icon: ICON_MAP.ARROW_UP_RIGHT,
            title: formatMessage(parkingStatusMessages.raiseGatewayBarrier, { entry: gatewayAction?.gateway.name }),
            description: formatMessage(parkingStatusMessages.raiseGatewayBarrierDescription),
            text: formatMessage(parkingStatusMessages.raiseGatewayBarrierText, { entry: gatewayAction?.gateway.name }),
            button: formatMessage(parkingStatusMessages.raise),
            onSubmit: () => openGateway({ parkingId, gatewayId: gatewayAction.gateway.id }),
        };
    }, [
        emergencyCloseGateway,
        emergencyOpenGateway,
        formatMessage,
        gatewayAction,
        lockGateway,
        openGateway,
        parkingId,
    ]);

    return (
        <Dialog modal open={isModalOpen} onOpenChange={() => setGatewayAction(null)}>
            <DialogContent>
                <DialogHeader className="flex-row gap-2">
                    {icon && <SquareIcon icon={icon} />}
                    <div>
                        <DialogTitle className="w-5/6">{title}</DialogTitle>
                        <DialogDescription>{description}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody className="text-gray-500">{text}</DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant={
                            gatewayAction?.action === GatewayAction.EMERGENCY ||
                            gatewayAction?.action === GatewayAction.EMERGENCY_CLOSE
                                ? 'destructiveLight'
                                : 'default'
                        }
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {button}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
